import { Component } from "@theme/component";
import { fetchConfig, onAnimationEnd, preloadImage } from "@theme/utilities";
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  VariantUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { morph } from "@theme/morph";

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 1500;
export const FLY_TO_CART_ANIMATION_DURATION = 450;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  /** @type {number[]} */
  #animationTimeouts = [];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("pointerenter", this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#animationTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.#animationTimeouts = [];
    this.removeEventListener("pointerenter", this.#preloadImage);
  }

  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  handleClick(event) {
    return;
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;
    if (!image) return;
    preloadImage(image);
  };

  /**
   * Spawns multiple fly-to-cart animations with staggered starts.
   * @param {number} count - Number of animations to spawn (default: 1)
   * @param {number} staggerDelay - Delay between each animation start in ms (default: 150)
   * @returns {Promise<void>} - Resolves when all animations complete
   */
  animateFlyToCart(count = 1, staggerDelay = 0) {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector(".header-actions__cart-icon");
    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) {
      return Promise.resolve();
    }

    // Track all animation promises
    const animationPromises = [];

    // Spawn multiple animations with staggered delays
    for (let i = 0; i < count; i++) {
      const animationPromise = new Promise((resolve) => {
        setTimeout(() => {
          const flyToCartElement = /** @type {FlyToCart} */ (
            document.createElement("fly-to-cart")
          );

          flyToCartElement.style.setProperty(
            "background-image",
            `url(${image})`
          );
          flyToCartElement.source = addToCartButton;
          flyToCartElement.destination = cartIcon;
          flyToCartElement.dataset.instanceId = `${Date.now()}-${i}`;

          // Listen for animation completion
          flyToCartElement.addEventListener("animationcomplete", () => {
            resolve();
          });

          document.body.appendChild(flyToCartElement);
        }, i * staggerDelay);
      });

      animationPromises.push(animationPromise);
    }

    // Return promise that resolves when all animations complete
    return Promise.all(animationPromises);
  }

  /**
   * Animates the add to cart button text.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    // Clear all existing timeouts
    this.#animationTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.#animationTimeouts = [];

    // Remove and re-add class to restart animation
    addToCartButton.classList.remove("atc-added");
    void addToCartButton.offsetWidth; // Force reflow
    addToCartButton.classList.add("atc-added");

    // Set timeout to remove class after animation completes
    const timeout = setTimeout(() => {
      addToCartButton.classList.remove("atc-added");
      this.#animationTimeouts = this.#animationTimeouts.filter(
        (t) => t !== timeout
      );
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);

    this.#animationTimeouts.push(timeout);
  }
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });
    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    if (
      this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute(
        "disabled"
      ) === "true"
    )
      return;

    const form = this.querySelector("form");
    if (!form) throw new Error("Product form element missing");

    const formData = new FormData(form);
    const isQuickAdd = event.target.closest(".quick-add-modal");
    const id = formData.get("id");
    if (!id) throw new Error("Form ID is required");
    const drawer = document.getElementsByClassName("cart-drawer__content");
    if (drawer[0]) {
      drawer[0].classList.add("keep-loading");
    }

    document.dispatchEvent(
      new CartAddEvent({}, id.toString(), {
        source: "product-form-component",
        itemCount:
          Number(formData.get("quantity")) ||
          Number(this.dataset.quantityDefault),
        productId: this.dataset.productId,
      })
    );
    try {
      // 🚀 STEP 1: Start network request AND animation in parallel
      const networkPromise = this.#addToCart(formData);
      const animationPromise =
        !isQuickAdd && this.refs.addToCartButtonContainer
          ? this.refs.addToCartButtonContainer.animateFlyToCart(1, 150)
          : Promise.resolve();

      // STEP 2: Wait for BOTH to complete
      const [response] = await Promise.all([animationPromise, networkPromise]);
      document.dispatchEvent(
        new CartAddEvent({}, id.toString(), {
          source: "product-form-component",
          itemCount:
            Number(formData.get("quantity")) ||
            Number(this.dataset.quantityDefault),
          productId: this.dataset.productId,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      cartPerformance.measureFromEvent("add:user-action", event);
      if (drawer[0]) {
        drawer[0].classList.remove("keep-loading");
      }
    }
  }

  /**
   * Makes the add to cart network request
   * @param {FormData} formData
   * @returns {Promise<any>}
   */
  async #addToCart(formData) {
    // Check if freebies already exist before appending
    const cart = await fetch("/cart.js").then((res) => res.json());

    const alreadyInCart = cart.items.some((item) => item.id === 42256668459173);
    if (!alreadyInCart) {
      formData.append("items[][id]", "42256668459173");
      formData.append("items[][quantity]", "1");
    }

    const alreadyInCart2 = cart.items.some(
      (item) => item.id === 42681208668325
    );
    if (!alreadyInCart2) {
      formData.append("items[][id]", "42681208668325");
      formData.append("items[][quantity]", "1");
    }

    // Get section IDs for cart updates
    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component"
    );
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    formData.append("sections", cartItemComponentsSectionIds.join(","));

    const fetchCfg = fetchConfig("javascript", { body: formData });

    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: { ...fetchCfg.headers, Accept: "text/html" },
    });

    return response.json();
  }

  /**
   * Handles cart add errors
   * @param {string} message
   * @param {FormData} formData
   * @param {Event} event
   */
  #handleError(message, formData, event) {
    const { addToCartTextError } = this.refs;

    // Clear any existing timeout immediately
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = undefined;
    }

    window.dispatchEvent(new CartErrorEvent(this.id, message));

    if (!addToCartTextError) return;

    addToCartTextError.classList.remove("hidden");
    const textNode = addToCartTextError.childNodes[2];
    if (textNode) {
      textNode.textContent = message;
    } else {
      const newTextNode = document.createTextNode(message);
      addToCartTextError.appendChild(newTextNode);
    }
    this.#setLiveRegionText(message);

    this.#timeout = setTimeout(() => {
      if (!addToCartTextError) return;
      addToCartTextError.classList.add("hidden");
      this.#clearLiveRegionText();
      this.#timeout = undefined;
    }, 10000);

    document.dispatchEvent(
      new CartAddEvent({}, this.id, {
        didError: true,
        source: "product-form-component",
        itemCount:
          Number(formData.get("quantity")) ||
          Number(this.dataset.quantityDefault),
        productId: this.dataset.productId,
      })
    );
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton =
      addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector(
      '[ref="addToCartButton"]'
    );

    if (!currentAddToCartButton) return;

    // Update the button state
    if (
      event.detail.resource == null ||
      event.detail.resource.available == false
    ) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute(
        "hidden",
        "true"
      );
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? "";

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute(
          "data-product-variant-media",
          productVariantMedia + "&width=100"
        );
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };

    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    // Add random variation to each animation for visual variety
    const randomOffset = Math.random() * 40 - 20; // Random between -20 and 20

    const controlPoint1 = {
      x: startPoint.x + randomOffset,
      y: startPoint.y - 200,
    };
    const controlPoint2 = {
      x: endPoint.x - 300 + randomOffset,
      y: endPoint.y - 100,
    };

    /** @type {number | null} */
    let startTime = null;
    const duration = FLY_TO_CART_ANIMATION_DURATION;

    this.style.opacity = "1";

    /**
     * @param {number} currentTime
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const position = bezierPoint(
        progress,
        startPoint,
        controlPoint1,
        controlPoint2,
        endPoint
      );

      this.style.setProperty("--x", `${position.x}px`);
      this.style.setProperty("--y", `${position.y}px`);

      const scale = 1 - progress * 0.5;
      this.style.setProperty("--scale", `${scale}`);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.style.opacity = "0";
        onAnimationEnd(this, () => {
          // Dispatch custom event when animation completes
          this.dispatchEvent(new CustomEvent("animationcomplete"));
          this.remove();
        });
      }
    };

    this.style.setProperty("--x", `${startPoint.x}px`);
    this.style.setProperty("--y", `${startPoint.y}px`);

    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get("fly-to-cart")) {
  customElements.define("fly-to-cart", FlyToCart);
}
